import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { GitCompareArrows, CheckCircle2, AlertTriangle } from 'lucide-react';

const fmt = (v) => `$${v.toLocaleString()}`;

const CompareField = ({ label, valA, valB }) => {
  const diff = valA !== valB;
  return (
    <tr style={{ borderBottom: '0.5px solid #f0f0f0' }}>
      <td style={{ padding: '4px 8px', fontSize: 10, color: '#aaa', width: 100 }}>{label}</td>
      <td style={{ padding: '4px 8px', fontSize: 11, color: '#111', background: diff ? '#fef2f2' : 'transparent' }}>{valA || '—'}</td>
      <td style={{ padding: '4px 8px', fontSize: 11, color: '#111', background: diff ? '#f0fdf4' : 'transparent' }}>{valB || '—'}</td>
    </tr>
  );
};

const ClaimComparisonDialog = ({ open, onOpenChange, claims, activeClaimId }) => {
  const [claimAId, setClaimAId] = useState(activeClaimId || '');
  const [claimBId, setClaimBId] = useState('');

  const a = claims.find(c => c.id === claimAId);
  const b = claims.find(c => c.id === claimBId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl p-0 gap-0 overflow-hidden" data-testid="claim-comparison-dialog">
        <DialogHeader className="p-4 pb-3 border-b">
          <DialogTitle className="flex items-center gap-2 text-base">
            <GitCompareArrows className="h-4 w-4" /> Claim Comparison
          </DialogTitle>
        </DialogHeader>

        <div className="p-4">
          {/* Selectors */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <p style={{ fontSize: 10, color: '#aaa', textTransform: 'uppercase', marginBottom: 4 }}>Claim A</p>
              <select value={claimAId} onChange={e => setClaimAId(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" style={{ fontSize: 12 }} data-testid="compare-select-a">
                <option value="">Select claim...</option>
                {claims.map(c => <option key={c.id} value={c.id}>{c.id} — {c.patient}</option>)}
              </select>
            </div>
            <div>
              <p style={{ fontSize: 10, color: '#aaa', textTransform: 'uppercase', marginBottom: 4 }}>Claim B</p>
              <select value={claimBId} onChange={e => setClaimBId(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" style={{ fontSize: 12 }} data-testid="compare-select-b">
                <option value="">Select claim...</option>
                {claims.filter(c => c.id !== claimAId).map(c => <option key={c.id} value={c.id}>{c.id} — {c.patient}</option>)}
              </select>
            </div>
          </div>

          {a && b ? (
            <div style={{ maxHeight: 400, overflowY: 'auto' }}>
              {/* Score comparison */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                {[a, b].map((cl, i) => (
                  <div key={i} style={{ padding: '12px 14px', borderRadius: 8, background: '#f9fafb', border: '0.5px solid #e8e8e8' }}>
                    <div className="flex items-center justify-between mb-1">
                      <span style={{ fontSize: 12, fontWeight: 500 }}>{cl.patient}</span>
                      <Badge style={{ fontSize: 9, border: 'none', background: cl.score >= 90 ? '#dcfce7' : cl.score >= 70 ? '#fffbeb' : '#fef2f2', color: cl.score >= 90 ? '#166534' : cl.score >= 70 ? '#92400e' : '#991b1b' }}>
                        {cl.score}%
                      </Badge>
                    </div>
                    <p style={{ fontSize: 10, color: '#888' }}>{cl.id} · {cl.payer} · {fmt(cl.total)}</p>
                    <div className="flex items-center gap-1 mt-2">
                      {cl.issues === 0 ? <CheckCircle2 style={{ width: 10, height: 10, color: '#22c55e' }} /> : <AlertTriangle style={{ width: 10, height: 10, color: '#f59e0b' }} />}
                      <span style={{ fontSize: 10, color: cl.issues === 0 ? '#166534' : '#92400e' }}>{cl.issues === 0 ? 'Clean' : `${cl.issues} issues`}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Field-by-field comparison */}
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #e8e8e8' }}>
                    <th style={{ padding: '4px 8px', fontSize: 10, color: '#aaa', textAlign: 'left', width: 100 }}>Field</th>
                    <th style={{ padding: '4px 8px', fontSize: 10, color: '#aaa', textAlign: 'left' }}>{a.id}</th>
                    <th style={{ padding: '4px 8px', fontSize: 10, color: '#aaa', textAlign: 'left' }}>{b.id}</th>
                  </tr>
                </thead>
                <tbody>
                  <CompareField label="Patient" valA={a.patient} valB={b.patient} />
                  <CompareField label="Payer" valA={a.payer} valB={b.payer} />
                  <CompareField label="Provider" valA={a.provider} valB={b.provider} />
                  <CompareField label="DOS" valA={a.dos} valB={b.dos} />
                  <CompareField label="Total" valA={fmt(a.total)} valB={fmt(b.total)} />
                  <CompareField label="Score" valA={`${a.score}%`} valB={`${b.score}%`} />
                  <CompareField label="Issues" valA={String(a.issues)} valB={String(b.issues)} />
                  <CompareField label="Status" valA={a.status} valB={b.status} />
                  <CompareField label="Diagnoses" valA={(a.dx||[]).map(d=>d.code).join(', ')} valB={(b.dx||[]).map(d=>d.code).join(', ')} />
                  <CompareField label="CPT Codes" valA={(a.lines||[]).map(l=>l.cpt).join(', ')} valB={(b.lines||[]).map(l=>l.cpt).join(', ')} />
                  <CompareField label="Pre-Auth" valA={a.providerInfo?.preAuth || 'None'} valB={b.providerInfo?.preAuth || 'None'} />
                  <CompareField label="Insurance" valA={a.patientInfo?.insType} valB={b.patientInfo?.insType} />
                </tbody>
              </table>

              {/* Checks comparison */}
              <div className="mt-4">
                <p style={{ fontSize: 10, fontWeight: 600, color: '#aaa', textTransform: 'uppercase', marginBottom: 6 }}>Validation Checks</p>
                <div className="grid grid-cols-2 gap-2">
                  {[a, b].map((cl, ci) => (
                    <div key={ci} className="space-y-1">
                      <p style={{ fontSize: 10, color: '#555', fontWeight: 500, marginBottom: 4 }}>{cl.id}</p>
                      {(cl.checks || []).map((ch, i) => (
                        <div key={i} className="flex items-start gap-1" style={{ fontSize: 10 }}>
                          {ch.pass ? <CheckCircle2 style={{ width: 10, height: 10, color: '#22c55e', flexShrink: 0, marginTop: 1 }} /> : <AlertTriangle style={{ width: 10, height: 10, color: '#f59e0b', flexShrink: 0, marginTop: 1 }} />}
                          <span style={{ color: ch.pass ? '#166534' : '#92400e', lineHeight: '1.3' }}>{ch.label.substring(0, 60)}{ch.label.length > 60 ? '...' : ''}</span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center py-12">
              <p style={{ fontSize: 13, color: '#aaa' }}>Select two claims above to compare</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ClaimComparisonDialog;
